import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FileText, Send, Ban, Scissors, Undo2 } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card, CardHeader } from '@/components/common/Card';
import { Modal } from '@/components/common/Modal';
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States';
import { TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { invoicesApi } from '@/services/api/endpoints/finance';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { ROUTES } from '@/app/router/routes';
import { PERMISSIONS } from '@/types/permissions';
import { formatDate, formatDateTime, formatMoney, orDash } from '@/utils/format';
import { InvoiceStatusBadge, Money } from './shared';

/**
 * One bill: what was charged, what happened to it afterwards, and what is left.
 *
 * <p>Lines and adjustments are shown separately and never merged. A family
 * looking at a reduced bill is entitled to see both the original charge and the
 * reason it came down, and an adjustment reversed later stays visible with its
 * reason rather than disappearing.
 */
export function InvoiceDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [writeOffOpen, setWriteOffOpen] = useState(false);
  const [writeOff, setWriteOff] = useState({ amount: '', reason: '' });
  const [reversing, setReversing] = useState<{ id: string; label: string } | null>(null);
  const [reversalReason, setReversalReason] = useState('');

  const query = useQuery({
    queryKey: queryKeys.invoices.detail(id),
    queryFn: () => invoicesApi.get(id),
    enabled: id !== '',
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all });
  };

  const issue = useMutation({
    mutationFn: () => invoicesApi.issue(id),
    onSuccess: () => { toast.success('Invoice issued'); refresh(); },
    onError: (error) => handleApiError(error),
  });

  const cancel = useMutation({
    mutationFn: () => invoicesApi.cancel(id, cancelReason.trim()),
    onSuccess: () => {
      toast.success('Invoice cancelled');
      setCancelling(false);
      setCancelReason('');
      refresh();
    },
    onError: (error) => { setCancelling(false); handleApiError(error); },
  });

  const applyWriteOff = useMutation({
    mutationFn: () =>
      invoicesApi.writeOff(id, {
        amount: Number(writeOff.amount),
        reason: writeOff.reason.trim(),
      }),
    onSuccess: () => {
      toast.success('Amount written off');
      setWriteOffOpen(false);
      setWriteOff({ amount: '', reason: '' });
      refresh();
    },
    onError: (error) => handleApiError(error),
  });

  const reverse = useMutation({
    mutationFn: (adjustmentId: string) =>
      invoicesApi.reverseAdjustment(id, adjustmentId, reversalReason.trim()),
    onSuccess: () => {
      toast.success('Adjustment reversed');
      setReversing(null);
      setReversalReason('');
      refresh();
    },
    onError: (error) => { setReversing(null); handleApiError(error); },
  });

  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }
  if (query.isPending) {
    return <LoadingState label="Loading the invoice" />;
  }

  const invoice = query.data;
  const currency = invoice.currencyCode;

  const facts: { label: string; value: string }[] = [
    { label: 'Student', value: `${orDash(invoice.studentName)} · ${orDash(invoice.gradeName)}` },
    { label: 'Billed to', value: invoice.guardianName ?? 'No payer on record' },
    { label: 'Period', value: orDash(invoice.billingPeriod) },
    { label: 'Issued', value: formatDate(invoice.issueDate) },
    { label: 'Due', value: formatDate(invoice.dueDate) },
    {
      label: 'Days overdue',
      value: invoice.daysOverdue > 0 ? String(invoice.daysOverdue) : 'Not overdue',
    },
  ];

  return (
    <div>
      <Link to={ROUTES.invoices}
            className="mb-3 inline-flex items-center gap-1.5 text-[13px] text-muted transition-colors hover:text-ink">
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
        All invoices
      </Link>

      <PageHeader
        title={invoice.invoiceNumber}
        description={invoice.cancellationReason ?? undefined}
        icon={<FileText className="h-5 w-5" aria-hidden="true" />}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <InvoiceStatusBadge status={invoice.status} />
            {invoice.status === 'DRAFT' && can(PERMISSIONS.INVOICE_ISSUE) && (
              <Button size="sm" loading={issue.isPending} onClick={() => issue.mutate()}>
                <Send className="h-3.5 w-3.5" aria-hidden="true" />
                Issue
              </Button>
            )}
            {invoice.outstandingAmount > 0 && invoice.status !== 'CANCELLED'
              && can(PERMISSIONS.INVOICE_UPDATE) && (
              <Button size="sm" variant="secondary" onClick={() => setWriteOffOpen(true)}>
                <Scissors className="h-3.5 w-3.5" aria-hidden="true" />
                Write off
              </Button>
            )}
            {invoice.status !== 'CANCELLED' && can(PERMISSIONS.INVOICE_CANCEL) && (
              <Button size="sm" variant="secondary" onClick={() => setCancelling(true)}>
                <Ban className="h-3.5 w-3.5" aria-hidden="true" />
                Cancel
              </Button>
            )}
          </div>
        }
      />

      <div className="mb-4 grid gap-4 lg:grid-cols-[1fr_320px]">
        <Card className="px-4 py-4">
          <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-3">
            {facts.map((fact) => (
              <div key={fact.label}>
                <dt className="text-[11px] font-semibold uppercase tracking-wider text-subtle">
                  {fact.label}
                </dt>
                <dd className="mt-0.5 text-sm text-ink">{fact.value}</dd>
              </div>
            ))}
          </dl>
        </Card>

        <Card className="px-4 py-4">
          <dl className="space-y-1.5 text-sm">
            <Row label="Subtotal" amount={invoice.subtotal} currency={currency} />
            {invoice.discountTotal > 0 && (
              <Row label="Discounts" amount={-invoice.discountTotal} currency={currency} />
            )}
            {invoice.scholarshipTotal > 0 && (
              <Row label="Scholarships" amount={-invoice.scholarshipTotal} currency={currency} />
            )}
            {invoice.lateFeeTotal > 0 && (
              <Row label="Late fees" amount={invoice.lateFeeTotal} currency={currency} />
            )}
            {invoice.taxTotal > 0 && (
              <Row label="Tax" amount={invoice.taxTotal} currency={currency} />
            )}
            <div className="border-t-2 border-line pt-1.5">
              <Row label="Total" amount={invoice.totalAmount} currency={currency} bold />
            </div>
            <Row label="Paid" amount={invoice.amountPaid} currency={currency} />
            <div className="border-t border-line pt-1.5">
              <Row label="Outstanding" amount={invoice.outstandingAmount} currency={currency} bold />
            </div>
          </dl>
        </Card>
      </div>

      <Card className="mb-4">
        <CardHeader title="Charges"
                    description="Copied at the moment the bill was raised, so a later price rise cannot rewrite it." />
        {invoice.items.length === 0 ? (
          <EmptyState title="No charges on this invoice" />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-line bg-surface-2">
                  {['Description', 'Category', 'Period'].map((h) => (
                    <th key={h} scope="col"
                        className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-subtle">
                      {h}
                    </th>
                  ))}
                  {['Unit', 'Qty', 'Discount', 'Tax', 'Total'].map((h) => (
                    <th key={h} scope="col"
                        className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-subtle">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {invoice.items.map((item) => (
                  <tr key={item.id}>
                    <td className="px-4 py-2.5 text-ink">{item.description}</td>
                    <td className="px-4 py-2.5 text-muted">{orDash(item.feeCategoryName)}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-subtle">
                      {orDash(item.periodKey)}
                    </td>
                    <td className="px-4 py-2.5"><Money amount={item.unitAmount} /></td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-muted">
                      {item.quantity}
                    </td>
                    <td className="px-4 py-2.5"><Money amount={item.discountAmount} /></td>
                    <td className="px-4 py-2.5"><Money amount={item.taxAmount} /></td>
                    <td className="px-4 py-2.5"><Money amount={item.lineTotal} bold /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Adjustments"
          description="What happened to this bill after it was issued. Reversed entries are kept, not deleted."
        />
        {invoice.adjustments.length === 0 ? (
          <EmptyState title="No adjustments"
                      description="Discounts, awards, late fees and write-offs appear here." />
        ) : (
          <ul className="divide-y divide-line">
            {invoice.adjustments.map((adjustment) => (
              <li key={adjustment.id} className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-3">
                <span className="w-[110px] shrink-0 text-[11px] font-semibold uppercase tracking-wider text-subtle">
                  {adjustment.adjustmentType.replaceAll('_', ' ').toLowerCase()}
                </span>
                <span className={`min-w-[90px] text-right tabular-nums ${
                  adjustment.reversed ? 'text-subtle line-through'
                    : adjustment.increasesTotal ? 'text-danger' : 'text-success'}`}>
                  {adjustment.increasesTotal ? '+' : '−'}
                  {formatMoney(adjustment.amount, currency).replace(`${currency} `, '')}
                </span>
                <span className="min-w-[180px] flex-1 text-[13px] text-ink">
                  {adjustment.reason}
                  {adjustment.reversed && adjustment.reversalReason !== undefined && (
                    <span className="block text-[11px] text-muted">
                      Reversed: {adjustment.reversalReason}
                    </span>
                  )}
                </span>
                <span className="text-[11px] tabular-nums text-subtle">
                  {formatDateTime(adjustment.appliedAt)}
                </span>
                {!adjustment.reversed && can(PERMISSIONS.INVOICE_UPDATE) && (
                  <Button size="sm" variant="ghost"
                          onClick={() => setReversing({
                            id: adjustment.id,
                            label: `${adjustment.adjustmentType.replaceAll('_', ' ').toLowerCase()} of ${formatMoney(adjustment.amount, currency)}`,
                          })}>
                    <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
                    Reverse
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Modal
        open={cancelling}
        title={`Cancel ${invoice.invoiceNumber}?`}
        description="Refused if any money has been taken against it — reverse the payment first."
        onClose={() => setCancelling(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCancelling(false)}>Keep it</Button>
            <Button variant="danger" loading={cancel.isPending}
                    disabled={cancelReason.trim() === ''}
                    onClick={() => cancel.mutate()}>
              Cancel invoice
            </Button>
          </>
        }
      >
        <TextField
          label="Reason"
          required
          placeholder="Family withdrew before term started"
          hint="Recorded on the invoice and in the audit trail. A cancelled bill without a reason is an unanswerable question later."
          value={cancelReason}
          onChange={(event) => setCancelReason(event.target.value)}
        />
      </Modal>

      <Modal
        open={writeOffOpen}
        title="Write off an amount"
        description={`${formatMoney(invoice.outstandingAmount, currency)} is currently outstanding.`}
        onClose={() => setWriteOffOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setWriteOffOpen(false)}>Cancel</Button>
            <Button loading={applyWriteOff.isPending}
                    disabled={writeOff.amount === '' || writeOff.reason.trim() === ''}
                    onClick={() => applyWriteOff.mutate()}>
              Write off
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <TextField label="Amount" type="number" step="0.01" required
                     value={writeOff.amount}
                     onChange={(event) => setWriteOff((w) => ({ ...w, amount: event.target.value }))}
                     hint="Cannot exceed what is outstanding." />
          <TextField label="Reason" required
                     placeholder="Uncollectable — family left the country"
                     value={writeOff.reason}
                     onChange={(event) => setWriteOff((w) => ({ ...w, reason: event.target.value }))} />
        </div>
      </Modal>

      <Modal
        open={reversing !== null}
        title="Reverse this adjustment?"
        description={reversing?.label}
        onClose={() => setReversing(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setReversing(null)}>Keep it</Button>
            <Button variant="danger" loading={reverse.isPending}
                    disabled={reversalReason.trim() === ''}
                    onClick={() => reversing !== null && reverse.mutate(reversing.id)}>
              Reverse
            </Button>
          </>
        }
      >
        <TextField
          label="Reason"
          required
          placeholder="Head waived the late fee"
          hint="The adjustment is marked reversed and kept — who reversed it, and why, gets asked."
          value={reversalReason}
          onChange={(event) => setReversalReason(event.target.value)}
        />
      </Modal>
    </div>
  );
}

function Row({ label, amount, currency, bold = false }: {
  label: string;
  amount: number;
  currency: string;
  bold?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className={bold ? 'font-semibold text-ink' : 'text-muted'}>{label}</dt>
      <dd className={`tabular-nums ${bold ? 'font-semibold text-ink' : 'text-ink'}`}>
        {formatMoney(amount, currency)}
      </dd>
    </div>
  );
}
